import { useEffect } from "react";
import { trackGuestClick } from "@/lib/clickTracking";

function isOutboundLink(el: HTMLAnchorElement) {
  const href = el.getAttribute("href") || "";
  if (!href) return false;
  if (href.startsWith("mailto:") || href.startsWith("tel:")) return true;
  if (href.startsWith("http://") || href.startsWith("https://")) {
    try {
      const url = new URL(href);
      return url.origin !== window.location.origin;
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Extract a clean, meaningful label from a clicked element (max 60 chars)
 */
function getClickLabel(el: HTMLElement): string | undefined {
  // Priority 1: aria-label
  const ariaLabel = el.getAttribute("aria-label");
  if (ariaLabel) return ariaLabel.slice(0, 60);

  // Priority 2: data-track-label
  const trackLabel = el.getAttribute("data-track-label");
  if (trackLabel) return trackLabel.slice(0, 60);

  // Priority 3: innerText (cleaned)
  const text = el.innerText?.trim().replace(/\s+/g, " ");
  if (text && text.length > 0 && text.length <= 60) return text;
  if (text && text.length > 60) return text.slice(0, 57) + "...";

  // Priority 4: title attribute
  const title = el.getAttribute("title");
  if (title) return title.slice(0, 60);

  return undefined;
}

function getElementType(el: HTMLElement): string {
  const tag = el.tagName.toLowerCase();
  if (tag === "a") return "link";
  if (tag === "button") return "button";
  if (el.getAttribute("role") === "button") return "button";
  if (el.closest("[data-track-cta]")) return "cta";
  return tag;
}

export function ClickTracker() {
  useEffect(() => {
    const handler = (evt: MouseEvent) => {
      const target = evt.target as HTMLElement | null;
      if (!target) return;

      // Defer tracking work to avoid blocking INP
      // Use requestIdleCallback if available, else setTimeout(0)
      const scheduleTracking = (fn: () => void) => {
        if ('requestIdleCallback' in window) {
          (window as any).requestIdleCallback(fn, { timeout: 2000 });
        } else {
          setTimeout(fn, 0);
        }
      };

      const anchor = target.closest("a") as HTMLAnchorElement | null;
      if (anchor) {
        const elementText = getClickLabel(anchor);
        const elementType = getElementType(anchor);

        if (isOutboundLink(anchor)) {
          scheduleTracking(() => {
            trackGuestClick({}, "outbound", { element_text: elementText, element_type: "outbound_link" });
          });
          return;
        }

        // Internal navigation clicks
        const href = anchor.getAttribute("href") || "";
        if (href && !href.startsWith("#") && !href.startsWith("mailto:") && !href.startsWith("tel:")) {
          scheduleTracking(() => {
            trackGuestClick({}, "internal_nav", { element_text: elementText, element_type: elementType });
          });
        }
        return;
      }

      const button = target.closest("button") as HTMLButtonElement | null;
      if (button) {
        const elementText = getClickLabel(button);
        const elementType = getElementType(button);
        scheduleTracking(() => {
          trackGuestClick({}, "button", { element_text: elementText, element_type: elementType });
        });
        return;
      }

      const cta = target.closest("[data-track-cta]") as HTMLElement | null;
      if (cta) {
        const elementText = getClickLabel(cta);
        scheduleTracking(() => {
          trackGuestClick({}, "cta", { element_text: elementText, element_type: "cta" });
        });
      }
    };

    document.addEventListener("click", handler, { capture: true, passive: true });
    return () => document.removeEventListener("click", handler, { capture: true } as any);
  }, []);

  return null;
}
